import _ from 'lodash'
import type { $Keys } from 'utility-types'

import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'
import companies from 'modules/core/store/collections/companies'

import notifiers from 'modules/automations/store/collections/notifiers'
import { Action } from 'modules/automations/store/models/Action'
import Notifier from 'modules/automations/store/models/Notifier'
import { Operation } from 'modules/automations/store/models/Operation'
import ContractVersion from 'modules/contracts/store/models/ContractVersion'
import CustomFieldsCollection from 'modules/custom_resources/store/collections/customFields/fields'
import Review from 'modules/documents/store/models/Review'
import payrollCollection from 'modules/payroll_bundle/store/collections/payroll'
import { ALLOWED_COUNTRIES } from 'modules/payroll_bundle/store/models/PayrollBundle'

const Actions = {
  requestReview: 'requestReview',
  sendReviewEmailToUser: 'sendReviewEmailToUser',
  sendDocumentEmailToUser: 'sendDocumentEmailToUser',
  sendEmployeeEmailToUserCreated: 'sendEmployeeEmailToUserCreated',
  sendEmployeeEmailToUserUpdated: 'sendEmployeeEmailToUserUpdated',
  sendEmployeeEmailToUserTerminated: 'sendEmployeeEmailToUserTerminated',
  sendLeaveEmailToUser: 'sendLeaveEmailToUser',
  sendCustomEmail: 'sendCustomEmail',
} as const

const Conditions = {
  author: 'author',
  assignee: 'assignee',
  documentPublic: 'documentPublic',
  documentType: 'documentType',
  leaveType: 'leaveType',
  documentAssignee: 'documentAssignee',
  reviewRequestState: 'reviewRequestState',
  employeeFirstName: 'employeeFirstName',
  employeeLastName: 'employeeLastName',
  employeeFullName: 'employeeFullName',
  employeeGender: 'employeeGender',
  employeeNationality: 'employeeNationality',
  employeeAge: 'employeeAge',
  employeeDateofBirth: 'employeeDateofBirth',
  employeeCity: 'employeeCity',
  employeeState: 'employeeState',
  employeeCountry: 'employeeCountry',
  employeeIdNumber: 'employeeIdNumber',
  employeeSocialSecurity: 'employeeSocialSecurity',
  employeeBankNumber: 'employeeBankNumber',
  employeeOffice: 'employeeOffice',
  employeeTeam: 'employeeTeam',
  employeeManager: 'employeeManager',
  employeeTimeoffManager: 'employeeTimeoffManager',
  employeeAddressLine1: 'employeeAddressLine1',
  employeeAddressLine2: 'employeeAddressLine2',
  employeePostalCode: 'employeePostalCode',
  startsOn: 'starts_on',
  endsOn: 'ends_on',
  jobTitle: 'job_title',
  hasPayroll: 'has_payroll',
  workingHours: 'working_hours',
  workingHoursFrequency: 'working_hours_frequency',
  salaryAmount: 'salary_amount',
  employeeActive: 'employeeActive',
} as const

export const Subcategories = {
  employeePersonalInfo: 'employeePersonalInfo',
  employeeAddress: 'employeeAddress',
  employeeIdAndBank: 'employeeIdAndBank',
  employeeCompanyDetails: 'employeeCompanyDetails',
  officeDetails: 'officeDetails',
  documents: 'documents',
  expenses: 'expenses',
  contract: 'contract',
  timeOff: 'timeOff',
  legalEntity: 'legalEntity',
  customFields: 'customFields',
  costCenterMemberships: 'costCenterMemberships',
} as const

const findSubcateogry = (usageGroup: string) => {
  switch (usageGroup) {
    case 'packages-legal-entity-info':
      return Subcategories.legalEntity

    case 'packages-office-info':
      return Subcategories.officeDetails

    case 'expenses':
      return Subcategories.expenses

    case 'documents':
      return Subcategories.documents

    case 'packages-employee-contract':
      return Subcategories.contract

    default:
      return Subcategories.customFields
  }
}

const customFieldConditions = async (): Promise<SelectableElement[]> => {
  const customFieldsCollection = new CustomFieldsCollection([], {
    model_ref: '',
    instance_id: '',
  })
  await customFieldsCollection.byAutomationsUsageGroup()

  return customFieldsCollection.map((field) => ({
    key: field.get('id'),
    text: () => field.get('label'),
    actionableType: Action.Types.branch,
    subcategory: findSubcateogry(field.get('usage_group_slug')),
    metadata: {
      operations: [
        {
          attribute: 'custom_fields',
          id: field.get('id'),
          operator: Operation.Operators.equals,
          value: null,
        },
      ],
    },
    isAction: false,
  }))
}

type AllowedConditions = $Keys<typeof Conditions>
type AllowedActions = $Keys<typeof Actions>

export interface SelectableElement {
  key: (AllowedActions | AllowedConditions) | null | undefined
  text: () => string
  subcategory?: keyof typeof Subcategories
  actionableType: string
  metadata?: Record<string, unknown>
  isAction: boolean
}

const employeePersonalConditions: SelectableElement[] = [
  {
    key: Conditions.employeeFirstName,
    text: () => i18n.t('automations.add_block.condition_types.first_name'),
    actionableType: Action.Types.branch,
    metadata: {
      operations: [
        {
          attribute: 'first_name',
          id: null,
          operator: Operation.Operators.equals,
          value: null,
        },
      ],
    },
    isAction: false,
  },
  {
    key: Conditions.employeeLastName,
    text: () => i18n.t('automations.add_block.condition_types.last_name'),
    actionableType: Action.Types.branch,
    metadata: {
      operations: [
        {
          attribute: 'last_name',
          id: null,
          operator: Operation.Operators.equals,
          value: null,
        },
      ],
    },
    isAction: false,
  },
  {
    key: Conditions.employeeFullName,
    text: () => i18n.t('automations.add_block.condition_types.full_name'),
    actionableType: Action.Types.branch,
    metadata: {
      operations: [
        {
          attribute: 'employee_id',
          id: null,
          operator: Operation.Operators.equals,
          value: null,
        },
      ],
    },
    isAction: false,
  },
  {
    key: Conditions.employeeGender,
    text: () => i18n.t('automations.add_block.condition_types.gender'),
    actionableType: Action.Types.branch,
    metadata: {
      operations: [
        {
          attribute: 'gender',
          id: null,
          operator: Operation.Operators.equals,
          value: null,
        },
      ],
    },
    isAction: false,
  },
  {
    key: Conditions.employeeNationality,
    text: () => i18n.t('automations.add_block.condition_types.nationality'),
    actionableType: Action.Types.branch,
    metadata: {
      operations: [
        {
          attribute: 'nationality',
          id: null,
          operator: Operation.Operators.equals,
          value: null,
        },
      ],
    },
    isAction: false,
  },
  {
    key: Conditions.employeeAge,
    text: () => i18n.t('automations.add_block.condition_types.age'),
    actionableType: Action.Types.branch,
    metadata: {
      operations: [
        {
          attribute: 'age',
          id: null,
          operator: Operation.Operators.equals,
          value: null,
        },
      ],
    },
    isAction: false,
  },
  {
    key: Conditions.employeeDateofBirth,
    text: () => i18n.t('automations.add_block.condition_types.date_of_birth'),
    actionableType: Action.Types.branch,
    metadata: {
      operations: [
        {
          attribute: 'birthday',
          id: null,
          operator: Operation.Operators.equals,
          value: null,
        },
      ],
    },
    isAction: false,
  },
].map((condition) => ({ ...condition, subcategory: Subcategories.employeePersonalInfo }))

const employeeAddressConditions: SelectableElement[] = [
  {
    key: Conditions.employeeCity,
    text: () => i18n.t('automations.add_block.condition_types.city'),
    actionableType: Action.Types.branch,
    metadata: {
      operations: [
        {
          attribute: 'city',
          id: null,
          operator: Operation.Operators.equals,
          value: null,
        },
      ],
    },
    isAction: false,
  },
  {
    key: Conditions.employeeState,
    text: () => i18n.t('automations.add_block.condition_types.state'),
    actionableType: Action.Types.branch,
    metadata: {
      operations: [
        {
          attribute: 'state',
          id: null,
          operator: Operation.Operators.equals,
          value: null,
        },
      ],
    },
    isAction: false,
  },
  {
    key: Conditions.employeeCountry,
    text: () => i18n.t('automations.add_block.condition_types.country'),
    actionableType: Action.Types.branch,
    metadata: {
      operations: [
        {
          attribute: 'country',
          id: null,
          operator: Operation.Operators.equals,
          value: null,
        },
      ],
    },
    isAction: false,
  },
  {
    key: Conditions.employeeAddressLine1,
    text: () => i18n.t('automations.add_block.condition_types.address_line_1'),
    actionableType: Action.Types.branch,
    metadata: {
      operations: [
        {
          attribute: 'address_line_1',
          id: null,
          operator: Operation.Operators.equals,
          value: null,
        },
      ],
    },
    isAction: false,
  },
  {
    key: Conditions.employeeAddressLine2,
    text: () => i18n.t('automations.add_block.condition_types.address_line_2'),
    actionableType: Action.Types.branch,
    metadata: {
      operations: [
        {
          attribute: 'address_line_2',
          id: null,
          operator: Operation.Operators.equals,
          value: null,
        },
      ],
    },
    isAction: false,
  },
  {
    key: Conditions.employeePostalCode,
    text: () => i18n.t('automations.add_block.condition_types.postal_code'),
    actionableType: Action.Types.branch,
    metadata: {
      operations: [
        {
          attribute: 'postal_code',
          id: null,
          operator: Operation.Operators.equals,
          value: null,
        },
      ],
    },
    isAction: false,
  },
].map((condition) => ({ ...condition, subcategory: Subcategories.employeeAddress }))

const employeeIdAndBankConditions: SelectableElement[] = [
  {
    key: Conditions.employeeIdNumber,
    text: () => i18n.t('automations.add_block.condition_types.id_number'),
    actionableType: Action.Types.branch,
    metadata: {
      operations: [
        {
          attribute: 'identifier',
          id: null,
          operator: Operation.Operators.equals,
          value: null,
        },
      ],
    },
    isAction: false,
  },
  {
    key: Conditions.employeeSocialSecurity,
    text: () => i18n.t('automations.add_block.condition_types.social_security_number'),
    actionableType: Action.Types.branch,
    metadata: {
      operations: [
        {
          attribute: 'social_security_number',
          id: null,
          operator: Operation.Operators.equals,
          value: null,
        },
      ],
    },
    isAction: false,
  },
  {
    key: Conditions.employeeBankNumber,
    text: () => i18n.t('automations.add_block.condition_types.bank_number'),
    actionableType: Action.Types.branch,
    metadata: {
      operations: [
        {
          attribute: 'bank_number',
          id: null,
          operator: Operation.Operators.equals,
          value: null,
        },
      ],
    },
    isAction: false,
  },
].map((condition) => ({ ...condition, subcategory: Subcategories.employeeIdAndBank }))

const employeeCompanyConditions: SelectableElement[] = [
  {
    key: Conditions.employeeOffice,
    text: () => i18n.t('automations.add_block.condition_types.office'),
    actionableType: Action.Types.branch,
    metadata: {
      operations: [
        {
          attribute: 'location_id',
          id: null,
          operator: Operation.Operators.equals,
          value: null,
        },
      ],
    },
    isAction: false,
  },
  {
    key: Conditions.employeeTeam,
    text: () => i18n.t('automations.add_block.condition_types.team'),
    actionableType: Action.Types.branch,
    metadata: {
      operations: [
        {
          attribute: 'team_id',
          id: null,
          operator: Operation.Operators.equals,
          value: null,
        },
      ],
    },
    isAction: false,
  },
  {
    key: Conditions.employeeManager,
    text: () => i18n.t('automations.add_block.condition_types.manager'),
    actionableType: Action.Types.branch,
    metadata: {
      operations: [
        {
          attribute: 'manager_id',
          id: null,
          operator: Operation.Operators.equals,
          value: null,
        },
      ],
    },
    isAction: false,
  },
  {
    key: Conditions.employeeTimeoffManager,
    text: () => i18n.t('automations.add_block.condition_types.timeoff_manager'),
    actionableType: Action.Types.branch,
    metadata: {
      operations: [
        {
          attribute: 'timeoff_manager_id',
          id: null,
          operator: Operation.Operators.equals,
          value: null,
        },
      ],
    },
    isAction: false,
  },
  {
    key: Conditions.employeeActive,
    text: () => i18n.t('automations.add_block.condition_types.employee_status'),
    actionableType: Action.Types.branch,
    metadata: {
      operations: [
        {
          attribute: 'active',
          id: null,
          operator: Operation.Operators.equals,
          value: null,
        },
      ],
    },
    isAction: false,
  },
].map((condition) => ({ ...condition, subcategory: Subcategories.employeeCompanyDetails }))

const leaveConditions: SelectableElement[] = [
  {
    key: Conditions.leaveType,
    text: () => i18n.t('automations.add_block.condition_types.leave_type'),
    actionableType: Action.Types.branch,
    metadata: {
      operations: [
        {
          attribute: 'leave_type_id',
          id: null,
          operator: Operation.Operators.equals,
          value: 'payslip',
        },
      ],
    },
    isAction: false,
  },
].map((condition) => ({ ...condition, subcategory: Subcategories.timeOff }))

// Returns either default fields or default + country-specific fields
const contractVersionFields = async () => {
  const contractVersionsFields = ContractVersion.Fields()

  if (!hasFeature('payroll_bundle')) {
    return Object.keys(contractVersionsFields).filter((field) => {
      const value = contractVersionsFields[field]
      if (!value) return false

      if ('countryCode' in value) {
        return !value.countryCode
      }

      return true
    })
  }

  await payrollCollection.fetch()

  const payrollConfig = payrollCollection.find({
    company_id: companies.current.attributes.id,
  })

  const payrollCountryCode = ALLOWED_COUNTRIES[payrollConfig?.attributes.country ?? 'other']

  return Object.keys(contractVersionsFields).filter((field) => {
    const value = contractVersionsFields[field]
    let countryCode = null

    if (value && 'countryCode' in value) {
      countryCode = value.countryCode
    }

    return !countryCode || countryCode === payrollCountryCode
  })
}

const contractVersionsConditions = async (): Promise<SelectableElement[]> => {
  const fields = await contractVersionFields()
  const contractVersionsFields = ContractVersion.Fields()

  return fields.map((field) => {
    const value = contractVersionsFields[field]!
    const { label, automationAttribute } = value

    return {
      key: field as any,
      text: () => label,
      subcategory: Subcategories.contract,
      actionableType: Action.Types.branch,
      metadata: {
        operations: [
          {
            attribute: automationAttribute || field,
            id: null,
            operator: Operation.Operators.equals,
            value: null,
          },
        ],
      },
      isAction: false,
    }
  })
}

export const conditionList = async (): Promise<SelectableElement[]> => {
  const customConditions = await customFieldConditions()
  const contractConditions = await contractVersionsConditions()

  const documentConditions: SelectableElement[] = [
    {
      key: Conditions.documentPublic,
      text: () => i18n.t('automations.add_block.condition_types.document_public'),
      subcategory: Subcategories.documents,
      actionableType: Action.Types.branch,
      metadata: {
        operations: [
          {
            attribute: 'document_public',
            id: null,
            operator: Operation.Operators.equals,
            value: true,
          },
        ],
      },
      isAction: false,
    },
    {
      key: Conditions.documentType,
      text: () => i18n.t('automations.add_block.condition_types.document_type'),
      actionableType: Action.Types.branch,
      metadata: {
        operations: [
          {
            attribute: 'document_type',
            id: null,
            operator: Operation.Operators.equals,
            value: null,
          },
        ],
      },
      isAction: false,
    },
    {
      key: Conditions.documentAssignee,
      text: () => i18n.t('automations.add_block.condition_types.document_assignee'),
      actionableType: Action.Types.branch,
      metadata: {
        operations: [
          {
            attribute: 'assignee_id',
            id: null,
            operator: Operation.Operators.equals,
            value: null,
          },
        ],
      },
      isAction: false,
    },
    {
      key: Conditions.author,
      text: () => i18n.t('automations.add_block.condition_types.document_author'),
      actionableType: Action.Types.branch,
      metadata: {
        operations: [
          {
            attribute: 'author_id',
            id: null,
            operator: Operation.Operators.equals,
            value: null,
          },
        ],
      },
      isAction: false,
    },
    {
      key: Conditions.reviewRequestState,
      text: () => i18n.t('automations.add_block.condition_types.review_request_state'),
      actionableType: Action.Types.branch,
      metadata: {
        operations: [
          {
            attribute: 'review_request_state',
            id: null,
            operator: Operation.Operators.equals,
            value: Review.Status.approved,
          },
        ],
      },
      isAction: false,
    },
  ].map((condition) => ({ ...condition, subcategory: Subcategories.documents }))

  return documentConditions.concat(
    customConditions,
    employeePersonalConditions,
    employeeAddressConditions,
    leaveConditions,
    employeeIdAndBankConditions,
    employeeCompanyConditions,
    contractConditions
  )
}

export const actionList = (triggerSlug: string) => {
  // isAction is needed to check if the resource is an action, because conditions and actions are
  // consider the same resource, and until now, we don't have the functionality to split them.
  const actions: SelectableElement[] = [
    {
      key: Actions.sendDocumentEmailToUser,
      text: () => i18n.t('automations.add_block.action_types.send_document_email_to'),
      actionableType: Action.Types.sendNotification,
      metadata: {
        notifier: Notifier.Slugs.documentCreated,
      },
      isAction: true,
    },
    {
      key: Actions.sendEmployeeEmailToUserCreated,
      text: () => i18n.t('automations.add_block.action_types.send_employee_created_email_to'),
      actionableType: Action.Types.sendNotification,
      metadata: {
        notifier: Notifier.Slugs.employeeCreated,
      },
      isAction: true,
    },
    {
      key: Actions.sendEmployeeEmailToUserUpdated,
      text: () => i18n.t('automations.add_block.action_types.send_employee_updated_email_to'),
      actionableType: Action.Types.sendNotification,
      metadata: {
        notifier: Notifier.Slugs.employeeUpdated,
      },
      isAction: true,
    },
    {
      key: Actions.sendEmployeeEmailToUserTerminated,
      text: () => i18n.t('automations.add_block.action_types.send_employee_terminated_email_to'),
      actionableType: Action.Types.sendNotification,
      metadata: {
        notifier: Notifier.Slugs.employeeTerminated,
      },
      isAction: true,
    },
    {
      key: Actions.sendLeaveEmailToUser,
      text: () => i18n.t('automations.add_block.action_types.send_leave_email_to'),
      actionableType: Action.Types.sendNotification,
      metadata: {
        notifier: Notifier.Slugs.leaveCreated,
      },
      isAction: true,
    },
    {
      key: Actions.sendLeaveEmailToUser,
      text: () => i18n.t('automations.add_block.action_types.send_leave_email_to'),
      actionableType: Action.Types.sendNotification,
      metadata: {
        notifier: Notifier.Slugs.leaveUpdated,
      },
      isAction: true,
    },
    {
      key: Actions.sendReviewEmailToUser,
      text: () => i18n.t('automations.add_block.action_types.send_review_email_to'),
      actionableType: Action.Types.sendNotification,
      metadata: {
        notifier: Notifier.Slugs.reviewRequestCompleted,
      },
      isAction: false, // FIXME: Review this, because it was put as false after a types refactor, but it looks off.
    },
    {
      key: Actions.sendCustomEmail,
      text: () => i18n.t('automations.add_block.action_types.send_custom_email'),
      actionableType: Action.Types.sendCustomNotification,
      metadata: {},
      isAction: true,
    },
  ]

  return actions.filter((action) => {
    if (!action.metadata) return true

    const notifier = notifiers.find({
      slug: action.metadata.notifier,
    })

    if (!notifier) return true

    return _.includes(notifier.attributes.triggers, triggerSlug)
  })
}
